import { BaseApiService } from '@/services/base-service';
import {
  SimpleEnumType,
  ApiPagination,
  BaseResponse,
  Filters,
  FiltersPagination,
  Response,
  TableOptions,
} from '@/types';
import { RepairStatuses } from '@/enums/statuses/repairs';
import { prepare_formdata, default_table_opts } from '@/utils';
import { Repairs } from '../types';

interface RepairsList extends BaseResponse, ApiPagination {
  data: {
    items: Repairs.Repair[];
    name_client_companies: { id: number | string; name: string }[];
    operation_types: { id: number | string; name: string }[];
    repair_types: { id: number | string; external_id: number | string; name: string }[];
    side_statuses: SimpleEnumType<RepairStatuses>[];
  };
}

interface RepairDiagnostics extends BaseResponse, ApiPagination {
  data: {
    diagnostic: Repairs.Diagnostics;
    symptom_types: { id: number | string; name: string }[];
    repair_types: { id: number | string; name: string }[];
    operation_types: { id: number | string; name: string }[];
    equipment_categories: { id: number | string; name: string }[];
    condition_code_types: { id: number | string; name: string }[];
  };
}

interface NewRepairCategories extends BaseResponse {
  data: {
    items: Repairs.Category[];
  };
}

interface NewRepairOperationTypes extends BaseResponse {
  data: {
    items: Repairs.OperationType[];
    is_not_warranty_repair: boolean;
    is_not_found: boolean;
  };
}

interface RepairsReturnZips extends BaseResponse, ApiPagination {
  data: {
    items: Repairs.ReturnZip[];
  };
}

interface NewRepairCreated extends BaseResponse {
  data: {
    order_side_id: number;
  };
}

interface ReparProductsUpdated extends BaseResponse {
  data: {
    items: Repairs.Product[];
  };
}

interface RepairsTransportCompanies extends BaseResponse {
  data: {
    items: Repairs.TransportCompany[];
  };
}

export class RepairsService extends BaseApiService {
  private readonly base_path = 'partner/service';

  // получение информации с сервера о заказ-нарядах

  async get_repairs(filters: FiltersPagination<Filters>): Promise<RepairsList> {
    const payload = filters;
    const url = `${this.base_path}/order-side`;

    const { data } = await this.http_client.get<RepairsList>(url, {
      params: {
        filters: payload.filters,
        limit: payload.pagination.limit,
        page: payload.pagination.page,
        sort: payload.pagination.sort,
        sort_type: payload.pagination.sort_type,
      },
    });

    return data;
  }

  async get_repair_by_id(id: string | number): Promise<Response<Repairs.DetailedInfo>> {
    const url = `${this.base_path}/order-side/${id}`;
    const { data } = await this.http_client.get<Response<Repairs.DetailedInfo>>(url);

    return data;
  }

  async get_repair_components_set(id: string | number): Promise<Response<Repairs.Component[]>> {
    const url = `${this.base_path}/order-side/${id}/complection`;
    const { data } = await this.http_client.get<Response<Repairs.Component[]>>(url);

    return data;
  }

  async add_new_component(
    id: string | number,
    payload: Repairs.ComponentPayload,
  ): Promise<BaseResponse> {
    const url = `${this.base_path}/order-side/${id}/complection`;

    const { data } = await this.http_client.post<BaseResponse>(url, payload);

    return data;
  }

  async delete_component(id: string | number): Promise<BaseResponse> {
    const url = `${this.base_path}/order-side/complection/${id}`;
    const { data } = await this.http_client.delete<BaseResponse>(url);

    return data;
  }

  async get_repair_diagnostics(id: string | number): Promise<RepairDiagnostics> {
    const url = `${this.base_path}/order-side/${id}/diagnostic`;
    const { data } = await this.http_client.get<RepairDiagnostics>(url);

    return data;
  }

  async upd_diagnostics(
    id: string | number,
    payload: Repairs.DiagnosticsPayload,
  ): Promise<BaseResponse> {
    const url = `${this.base_path}/order-side/${id}/diagnostic`;

    const { data } = await this.http_client.patch<BaseResponse>(url, payload);

    return data;
  }

  async get_repair_stages(id: string | number): Promise<Response<Repairs.Stage[]>> {
    const url = `${this.base_path}/order-side/${id}/status`;
    const { data } = await this.http_client.get<Response<Repairs.Stage[]>>(url);

    return data;
  }

  async add_repair_stage(
    id: string | number,
    payload: Repairs.StagePayload,
  ): Promise<BaseResponse> {
    const url = `${this.base_path}/order-side/${id}/status`;

    const { data } = await this.http_client.post<BaseResponse>(url, payload);

    return data;
  }

  async upd_repair_stage(
    id: string | number,
    payload: Pick<Repairs.Stage, 'comment'>,
  ): Promise<BaseResponse> {
    const url = `${this.base_path}/order-side/status/${id}`;

    const { data } = await this.http_client.patch<BaseResponse>(url, payload);

    return data;
  }

  async delete_repair_stage(id: string | number): Promise<BaseResponse> {
    const url = `${this.base_path}/order-side/status/${id}`;
    const { data } = await this.http_client.delete<BaseResponse>(url);

    return data;
  }

  async get_repair_products(id: string | number): Promise<Response<Repairs.Product[]>> {
    const url = `${this.base_path}/order-side/${id}/zip`;
    const { data } = await this.http_client.get<Response<Repairs.Product[]>>(url);

    return data;
  }

  // async get_possible_repair_products(
  //   id: string | number,
  // ): Promise<Response<Repairs.ProductDetails[]>> {
  //   const url = `${this.base_path}/order-side/${id}/zip/product`;
  //   const { data } = await this.http_client.get<Response<Repairs.ProductDetails[]>>(url);

  //   return data;
  // }

  // #TODO: remove
  async search_repair_products(search: string): Promise<Response<Repairs.ProductDetails[]>> {
    const url = `${this.base_path}/order-side/installed-components`;
    const { data } = await this.http_client.get<Response<Repairs.ProductDetails[]>>(url, {
      params: { city: search },
    });

    return data;
  }

  async upd_repair_products(
    id: string | number,
    payload: Repairs.UpdateProductPayload,
  ): Promise<ReparProductsUpdated> {
    const url = `${this.base_path}/order-side/zip/${id}`;

    const { data } = await this.http_client.patch<ReparProductsUpdated>(url, payload);

    return data;
  }

  async add_repair_product(
    id: string | number,
    payload: Repairs.AddProductPayload,
  ): Promise<BaseResponse> {
    const url = `${this.base_path}/order-side/${id}/zip`;

    const { data } = await this.http_client.post<BaseResponse>(url, payload);

    return data;
  }

  async delete_repair_product(id: string | number): Promise<BaseResponse> {
    const url = `${this.base_path}/order-side/zip/${id}`;
    const { data } = await this.http_client.delete<BaseResponse>(url);

    return data;
  }

  async get_repair_documents(id: string | number): Promise<Response<Repairs.Document[]>> {
    const url = `${this.base_path}/order-side/${id}/document`;
    const { data } = await this.http_client.get<Response<Repairs.Document[]>>(url);

    return data;
  }

  async delete_repair_document(id: string | number): Promise<BaseResponse> {
    const url = `${this.base_path}/order-side/document/${id}`;
    const { data } = await this.http_client.delete<BaseResponse>(url);

    return data;
  }

  async send_repair_document(id: string | number, file: File): Promise<BaseResponse> {
    const url = `${this.base_path}/order-side/${id}/document`;
    const { data } = await this.http_client.post<BaseResponse>(url, prepare_formdata({ file }));

    return data;
  }
  // NEW REPAIR

  async get_new_repair_categories(): Promise<NewRepairCategories> {
    const url = `${this.base_path}/order-side/categories`;
    const { data } = await this.http_client.get<NewRepairCategories>(url);

    return data;
  }

  async get_new_repair_operation_types(
    serial_number: string,
    organization_id: number,
  ): Promise<NewRepairOperationTypes> {
    const url = `${this.base_path}/order-side/operation_types?serial_number=${serial_number}&organization_id=${organization_id}`;
    const { data } = await this.http_client.get<NewRepairOperationTypes>(url);

    return data;
  }

  async send_new_repair(payload: Repairs.NewRepair): Promise<NewRepairCreated> {
    const url = `${this.base_path}/order-side`;
    const { data } = await this.http_client.post<NewRepairCreated>(url, prepare_formdata(payload));

    return data;
  }

  async edit_new_repair(id: string | number, payload: Repairs.NewRepair): Promise<NewRepairCreated> {
    const url = `${this.base_path}/order-side/${id}`;
    const { data } = await this.http_client.patch<NewRepairCreated>(url, payload);

    return data;
  }

  // warranty

  async check_warranty(id: string | number): Promise<Response<Repairs.Warranty[]>> {
    const url = `${this.base_path}/warranty/${id}`;
    const { data } = await this.http_client.get<Response<Repairs.Warranty[]>>(url);

    return data;
  }
  // reports

  async get_repairs_zip(filters: FiltersPagination<Filters>): Promise<RepairsReturnZips> {
    const payload = filters;
    const url = `${this.base_path}/return_zips`;

    const { data } = await this.http_client.get<RepairsReturnZips>(url, {
      params: {
        filters: payload.filters,
        limit: payload.pagination.limit,
        page: payload.pagination.page,
        sort: payload.pagination.sort,
        sort_type: payload.pagination.sort_type,
      },
    });

    return data;
  }

  async get_transport_companies(): Promise<RepairsTransportCompanies> {
    const url = `${this.base_path}/transport_companies`;
    const { data } = await this.http_client.get<RepairsTransportCompanies>(url);

    return data;
  }

  async send_return_zips(payload: Repairs.UpdReturnZips): Promise<BaseResponse> {
    const url = `${this.base_path}/return_zips?_method=patch`;
    const { data } = await this.http_client.post<BaseResponse>(url, payload);

    return data;
  }

  async get_catalog_products(payload: Repairs.CatalogProductRequest): Promise<Repairs.CatalogProductResponse> {
    const payload_filters = payload.filters;

    payload_filters.search = payload_filters.search ? payload_filters.search : '';

    const { data } = await this.http_client.get<Repairs.CatalogProductResponse>(`${this.base_path}/order-side/catalog_products`, {
      params: {
        filters: payload_filters,
        filters_exist: payload.filters_exist,
        limit: payload.pagination.limit,
        page: payload.pagination.page,
        sort: payload.pagination.sort,
        sort_type: payload.pagination.sort_type,
      },
    });

    return data;
  }
}

export const default_new_component = (): Repairs.ComponentPayload => ({
  nomenclature: '',
  comment: '',
});

export const default_reapairs_table_opts = (): TableOptions => ({
  ...default_table_opts(),
  sortBy: [''],
  sortDesc: [true],
});

export const default_new_repair = (): Repairs.NewRepair => ({
  client_id: 0,
  organization_id: 0,
  equipment_category_id: 0,
  operation_type_id: 0,
  nomenclature: '',
  serial_number: '',
  malfunction: '',
  complections: [],
  files: [],
  expired_date: '',
  is_product_not_found: false,
});

export const default_repair_details = (): Repairs.DetailedInfo => ({
  id: 0,
  approval_status: '',
  client: null,
  company: null,
  create_at: null,
  operation_type: null,
  nomenclature: null,
  organization: null,
  repair_type: null,
  serial_number: null,
  contract: null,
  status: null,
  organization_id: 0,
  expired_date: null,
  is_available_editing: false,
  operation_type_id: null,
  work_price_rub: null,
  operation_type_slug: null,
  contact_person: null,
});

export const default_catalog_products_filters = (): Repairs.CatalogProductRequestFilters => ({
  search: '',
  categories: [],
  software_type: null,
  equipment_models: [],
  not_active_only: false,
});
